import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import type { Task, TaskUser, TaskUserRequest } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, toValue } from 'vue'
import { storeToRefs } from 'pinia'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { AuToast } from '~/stores/toasts'
import useGroupsStore from '~/stores/groups'
import useAuthStore from '~/stores/auth'
import { TaskStatus } from '~/models/AssignTask'
import { useTasksApi } from '~/api/tasksApi'
import { useSseApi } from '~/api/sseApi'

interface UseAssignTaskOptions {
  tags?: MaybeRefOrGetter<string[] | undefined>
  additionalParams?: ComputedRef<Record<string, any>>
}

export function useAssignTask(options: UseAssignTaskOptions = {}) {
  const {
    getTasks: apiGetTasks,
    createTask: apiCreateTask,
    updateTask: apiUpdateTask,
    deleteTask: apiDeleteTask,
    closeTask: apiCloseTask,
    openTask: apiOpenTask,
    getTaskUsers: apiGetTaskUsers,
    addTaskUsers: apiAddTaskUsers,
    updateTaskUser: apiUpdateTaskUser,
  } = useTasksApi()

  const { t } = useI18n()
  const { createStream, closeStream, notifyStream } = useSseApi()
  const { groups, hasLoadedGroups } = storeToRefs(useGroupsStore())
  const { isAuthenticated, isTeacher, hasLoadedUser } = storeToRefs(useAuthStore())

  const queryClient = useQueryClient()

  const tags = computed(() => toValue(options.tags) || [])
  const additionalParams = options.additionalParams

  const resolvedAdditionalParams = computed(() => additionalParams?.value ?? {})

  const resolvedGroupIds = computed(() => {
    const groupIdFromParams = resolvedAdditionalParams.value.groupId
    const ids = Array.isArray(groupIdFromParams)
      ? groupIdFromParams
      : (groupIdFromParams ? [groupIdFromParams] : groups.value.map(g => g.groupId))

    return [...new Set(ids)].sort()
  })

  const baseQueryKey = ['assigned_tasks'] as const

  const queryKey = computed(() => [
    ...baseQueryKey,
    {
      groups: resolvedGroupIds.value,
      tags: [...tags.value].sort(),
      isTeacher: !!isTeacher.value,
      ...resolvedAdditionalParams.value,
    },
  ])

  const {
    data: tasks,
    isLoading,
    isError,
    refetch: refetchTasks,
  } = useQuery({
    enabled: computed(() =>
      isAuthenticated.value
      && hasLoadedUser.value
      && hasLoadedGroups.value
      && Array.isArray(tags.value)
      && tags.value.length > 0
    ),
    queryKey,
    queryFn: () => {
      const baseParams = isTeacher.value ? { groups: resolvedGroupIds.value, tags: tags.value } : { tags: tags.value }
      const params = { ...baseParams, ...resolvedAdditionalParams.value }
      return apiGetTasks(params)
    },
    select: ({ response }) => response.sort((a, b) =>
      new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime()
    ),
    staleTime: Infinity,
    retry: 1,
  })

  const createTask = useMutation({
    mutationFn: async (task: Partial<Task>) => apiCreateTask(task),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
    },
    onSettled: (_, error) => error
      ? AuToast.error(t('assign.toasts.createError'))
      : AuToast.success(t('assign.toasts.createSuccess')),
  })

  const updateTask = useMutation({
    mutationFn: async (task: Task) => apiUpdateTask(task),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
    },
  })

  const deleteTask = useMutation({
    mutationFn: async (task: Task) => apiDeleteTask(task),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
      await closeStream(String(variables.taskId))
    },
    onSettled: (_, error) => error
      ? AuToast.error(t('assign.toasts.deleteError'))
      : AuToast.success(t('assign.toasts.deleteSuccess')),
  })

  const closeTask = useMutation({
    mutationFn: async (task: Task) => apiCloseTask(task),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
      await notifyStream(String(variables.taskId), { id: crypto.randomUUID(), status: TaskStatus.Closed, all: true })
    },
    onSettled: (_, error) => error
      ? AuToast.error(t('assign.toasts.closeError'))
      : AuToast.success(t('assign.toasts.closeSuccess')),
  })

  const openTask = useMutation({
    mutationFn: async (task: Task) => apiOpenTask(task),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
      await createStream(String(variables.taskId))
    },
    onSettled: (_, error) => error
      ? AuToast.error(t('assign.toasts.openError'))
      : AuToast.success(t('assign.toasts.openSuccess')),
  })

  const getTaskUsers = useMutation({
    mutationFn: async (task: Task) => apiGetTaskUsers(task),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
    },
  })

  const addTaskUsers = useMutation({
    mutationFn: async ({ task, users }: {
      task: Task
      users: TaskUserRequest
    }) => apiAddTaskUsers(task, users),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
    },
    onSettled: (_, error) => error
      ? AuToast.error(t('assign.toasts.saveError'))
      : AuToast.success(t('assign.toasts.saveSuccess')),
  })

  const updateTaskUser = useMutation({
    mutationFn: async ({ task, user, status }: {
      task: Task
      user: TaskUser
      status: TaskStatus
    }) => apiUpdateTaskUser(task, user, status),

    onMutate: async ({ task, user, status }) => {
      const currentQueryKey = queryKey.value

      await queryClient.cancelQueries({ queryKey: currentQueryKey })

      const previousTasks = queryClient.getQueryData(currentQueryKey)

      queryClient.setQueryData(currentQueryKey, (oldData: Task[]) => {
        return oldData?.map((t: Task) => {
          if (t.taskId === task.taskId) {
            return {
              ...t,
              users: t.users?.map(u =>
                u.userId === user.userId ? { ...u, status } : u
              ),
            }
          }
          return t
        })
      })

      return { previousTasks, queryKey: currentQueryKey }
    },

    onError: (err, variables, context) => {
      console.error('Failed to update task user:', {
        error: err,
        taskId: variables.task.taskId,
        userId: variables.user.userId,
        status: variables.status
      })
      if (context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context?.previousTasks)
      }
    },

    onSuccess: async (_, variables) => {
      await notifyStream(String(variables.task.taskId), {
        id: crypto.randomUUID(),
        status: variables.user.status === TaskStatus.Open ? 'CLOSED' : 'OPEN',
        all: false,
        userId: String(variables.user.userId),
      })
    },

    onSettled: async (_, error, variables) => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
      return error
        ? AuToast.error(t('assign.toasts.updateUserError'))
        : AuToast.success(t('assign.toasts.updateUserSuccess', {
            status: variables.user.status === TaskStatus.Open ? t('assign.closed').toLowerCase() : t('assign.opened').toLowerCase(),
            user: variables.user.fullName,
          }))
    },
  })

  const addTaskUsersAndOpen = useMutation({
    mutationFn: async ({ task, users }: {
      task: Task
      users: TaskUserRequest
    }) => {
      await apiUpdateTask(task)
      await apiAddTaskUsers(task, users)
      await apiOpenTask(task)
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
      await createStream(String(variables.task.taskId))
    },
    onSettled: (_, error) => error
      ? AuToast.error(t('assign.toasts.addUsersAndOpenError'))
      : AuToast.success(t('assign.toasts.addUsersAndOpenSuccess')),
  })

  const shareSimple = useMutation({
    mutationFn: async ({ newTask, users }: {
      newTask: Partial<Task>
      users: TaskUserRequest
    }) => {
      const task = await apiCreateTask(newTask)
      await apiAddTaskUsers(task, users)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: baseQueryKey })
    },
    onSettled: (_, error) => error
      ? AuToast.error(t('share.toasts.createError'))
      : AuToast.success(t('share.toasts.createSuccess')),
  })

  return {
    tasks,
    isLoading,
    isError,
    refetchTasks,
    createTask,
    updateTask,
    deleteTask,
    closeTask,
    openTask,
    getTaskUsers,
    addTaskUsers,
    updateTaskUser,
    addTaskUsersAndOpen,
    shareSimple,
  }
}
